import { objectToUrlParams, template } from './utils';

/**
 * @param lat
 * @param lng
 * @param zoom
 * @param map
 */
export function centerMap(lat, lng, zoom, map = throwIfUndefined('map')) {
    return new Promise((resolve) => {
        window.google.maps.event.addListenerOnce(map, 'idle', resolve);
        console.log(lat, lng);
        map.setCenter(new window.google.maps.LatLng(lat, lng));
        map.setZoom(zoom);
    });
}

/**
 * @param markers
 */
export function clearMarkers(markers) {
    markers.forEach((marker) => {
        marker.setMap(null);
    });
}

/**
 * @param lat
 * @param lng
 * @param radius
 * @param map
 * @return {window.google.maps.Circle}
 */
export function createCircle(lat, lng, map = throwIfUndefined('map'), radius = 5000, color = '#213B87') {
    const options = {
        center: new window.google.maps.LatLng(lat, lng),
        fillColor: color,
        fillOpacity: 0.15,
        map,
        radius,
        strokeColor: color,
        strokeOpacity: 0.2,
        strokeWeight: 1,
    };

    return new window.google.maps.Circle(options);
}

/**
 * @link https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/markerclusterer.js
 * @param markers
 * @param map
 * @param options
 */
export function createClusters(markers, map = throwIfUndefined('map'), options = {}) {
    return new window.MarkerClusterer(map, markers, options);
}

/**
 * @param marker
 * @param map
 * @param options
 * @return {{active: boolean, toggle(): void, id: *, closeAll(*=): void, close(*=): (undefined), open(): (undefined)}}
 */
export function createInfowindow(marker, map = throwIfUndefined('map'), options = {}) {
    options = {
        data: {},
        template: '',
        ...options,
    };
    return {
        active: false,
        id: marker.id,
        close(fireEvent = true) {
            if (!this.active) {
                return;
            }

            this.active = false;
            map.infoWindow.close(map, marker);

            if (typeof options.onClose === 'function' && fireEvent) {
                options.onClose(null);
            }
        },
        closeAll(fireEvent = true) {
            window.google.maps.event.trigger(map.infoWindow, 'closeall', fireEvent);
        },
        open(fireEvent = true) {
            if (this.active) {
                return;
            }

            this.closeAll(false);

            map.infoWindow.setContent(template(options.template, options.data));
            this.active = true;
            map.infoWindow.open(map, marker);

            window.google.maps.event.addListener(map.infoWindow, 'closeall', (fireEvent = true) => {
                this.close(fireEvent);
            });
            window.google.maps.event.addListener(map.infoWindow, 'closeclick', () => {
                this.close();
            });

            if (typeof options.onOpen === 'function' && fireEvent) {
                options.onOpen(options.data);
            }
        },
        toggle() {
            if (this.active) {
                this.close();
            } else {
                this.open();
            }
        },
    };
}

/**
 * @param element
 * @param options
 * @return {Map<any, any>}
 */
export function createMap(element, options = {}) {
    options = {
        center: {
            lat: 50.6740609,
            lng: 4.470022,
        },
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        infoWindow: new window.google.maps.InfoWindow({ maxWidth: 500 }),
        mapTypeControl: false,
        mapTypeControlOptions: false,
        scrollwheel: false,
        streetViewControl: false,
        styles: null,
        zoom: 11,
        zoomControlOptions: {
            position: window.google.maps.ControlPosition.TOP_RIGHT,
            style: window.google.maps.ZoomControlStyle.SMALL,
        },
        ...options,
    };

    return new window.google.maps.Map(element, options);
}

/**
 * @param item
 * @param map
 * @return {window.google.maps.Marker}
 */
export function createMarker(item, map = throwIfUndefined('map')) {
    const options = {
        icon: item.icon,
        id: item.id,
        map,
        position: new window.google.maps.LatLng(item.lat, item.lng),
        title: item.title,
    };

    return new window.google.maps.Marker(options)
}

/**
 * @param markers
 * @param mapOrBounds
 * @return {*}
 */
export function filterByBounds(markers, mapOrBounds = throwIfUndefined('mapOrBounds')) {
    if (typeof mapOrBounds.getBounds === 'function') {
        mapOrBounds = map.getBounds();
    }
    return markers.filter((marker) => mapOrBounds.contains(marker.getPosition()));
}

/**
 * @param markers
 * @param options
 * @return {*}
 */
export function filterByDistance(markers, options = {}) {
    options = {
        distance: 5000,
        lat: null,
        lng: null,
        ...options,
    };

    return markers.filter((marker) => {
        const position = marker.getPosition();
        const distanceBetween = window.google.maps.geometry.spherical.computedDistanceBetween(
            position,
            new window.google.maps.LatLng(options.lat, options.lng)
        );

        if (options.distance) {
            return options.distance >= distanceBetween;
        }

        return true;
    });
}

/**
 * @param options
 * @param map
 * @return {Promise<unknown>}
 */
export function geolocateCenter(options = {}, map = throwIfUndefined('map')) {
    options = {
        center: true,
        circle: true,
        marker: true,
        ...options,
    };
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                if (options.marker) {
                    createMarker({
                        icon: {
                            path: 'M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z',
                            fillColor: '#909',
                            fillOpacity: 1,
                            strokeColor: '',
                            strokeWeight: 0,
                        },
                        id: null,
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                        title: null,
                    }, map);
                }

                if (options.circle) {
                    createCircle(position.coords.latitude, position.coords.longitude, map);
                }

                if (options.center) {
                    centerMap(position.coords.latitude, position.coords.longitude, 12, map);
                }

                resolve(position);
            }, reject);
        } else {
            reject();
        }
    });
}

let isLoading = false;

/**
 * @param key
 * @param options
 * @param dependencies
 * @return {Promise<unknown>}
 */
export function loadGoogleMaps(key, options = {}, dependencies = []) {
    function isReady(callback) {
        if (window.google && window.google.maps) {
            callback(window.google.maps);
            isLoading = false;
        }
    }

    // console.log('---------------------------');
    // console.log(key);

    return new Promise((resolve) => {
        if (window.google && window.google.maps) {
            return resolve(window.google.maps);
        }

        options = {
            language: 'en',
            version: '3.18',
            ...options,
        };

        if (isLoading) {
            const interval = setInterval(() => {
                isReady(() => clearInterval(interval));
            }, 50);
        } else {
            isLoading = true;
            const callbackName = '__google_maps_api_provider__';
            window[callbackName] = () => isReady(resolve);
            dependencies = [`https://maps.googleapis.com/maps/api/js?${objectToUrlParams({
                callback: callbackName,
                key,
                ...options,
            })}`].concat(dependencies);

            loadScripts(dependencies).then(() => isReady(resolve));
        }
    });
}

/**
 * @param urls
 * @return {Promise<unknown>}
 */
function loadScripts(urls) {
    if (typeof urls === 'string') {
        urls = [urls];
    }

    return new Promise((resolve, reject) => {
        let count = urls.length;

        urls.forEach((url) => {
            loadScript(url).then(() => {
                count--;

                if (count < 1) {
                    resolve();
                }
            })
        });
    });
}

/**
 * @param url
 * @return {Promise<unknown>}
 */
function loadScript(url) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        script.src = url;
        script.type = 'text/javascript';
        document.body.appendChild(script);
    });
}

/**
 * @param lat
 * @param lng
 * @param map
 */
export function moveTo(lat, lng, map = throwIfUndefined('map')) {
    return new Promise((resolve) => {
        window.google.maps.event.addListenerOnce(map, 'idle', resolve);
        map.panTo(new window.google.maps.LatLng(lat, lng));
    });
}

/**
 * Throw a message exception if value is null or undefined.
 * @param {String} paramName - Parameter name
 * @param {String} exception - Exception message
 * @throws {Error}
 * @example
 * function fooMethod(value = throwIfUndefined('value')) {
 *    // ...
 * }
 */
export function throwIfUndefined(paramName, exception = `Missing parameter: ${paramName}`) {
    throw new Error(exception);
}
